import {
  Badge,
  Box,
  Divider,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useColorModeValue,
} from '@chakra-ui/react'
import React from 'react'
import { Pessoa } from './types'

/**
 * Ficha do participante — os campos que o briefing manda exibir na conferência.
 *
 * A tabela mostra nome, CPF e e-mail; o briefing (pág. 3) pede treze campos. Treze colunas
 * deixariam a tabela ilegível no balcão, e o que o atendente faz ali não é varrer colunas:
 * é confirmar que a pessoa na frente dele é a do cadastro. Daí uma ficha por participante.
 *
 * Nada disso exigiu mexer na API: a importação já grava tudo em customFields e o
 * Credenciamento.tsx já repassa o objeto inteiro para a tela.
 */

/**
 * Data como o Brasil lê.
 *
 * `dataNascimento` é gravada como texto "aaaa-mm-dd" pela importação, e chegava assim na
 * ficha — formato que ninguém no balcão lê de relance. A conversão é feita na string, e não
 * por `Date`: uma data sem hora interpretada por fuso volta um dia (17/02 vira 16/02).
 */
const dataBr = (v: unknown) => {
  const t = String(v ?? '').trim()
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : t
}

/** Rótulo e origem de cada campo, na ordem em que o briefing os lista. */
const CAMPOS: Array<{ rotulo: string; ler: (p: Pessoa) => unknown }> = [
  { rotulo: 'Nome completo', ler: p => p.nome },
  { rotulo: 'Tipo de ingresso', ler: p => p.tipoIngresso || p.customFields?.tipoIngresso },
  { rotulo: 'E-mail', ler: p => p.email },
  { rotulo: 'CPF / CNPJ', ler: p => p.customFields?.cnpj || p.cpf },
  { rotulo: 'Data de nascimento', ler: p => dataBr(p.customFields?.dataNascimento) },
  { rotulo: 'Conselho profissional', ler: p => p.customFields?.conselhoProfissional },
  { rotulo: 'Registro profissional', ler: p => p.customFields?.registroProfissional },
  { rotulo: 'Empresa atual', ler: p => p.customFields?.empresa },
  { rotulo: 'Telefone (WhatsApp)', ler: p => p.telefone || p.customFields?.telefoneAlternativo },
  { rotulo: 'Estado', ler: p => p.customFields?.estadoUf },
  { rotulo: 'Associado da SBAM', ler: p => p.customFields?.associadoSbam },
  { rotulo: 'CFM', ler: p => p.customFields?.cfmNumero },
  { rotulo: 'Nacionalidade', ler: p => p.customFields?.nacionalidade },
  { rotulo: 'Passaporte / NIF', ler: p => p.customFields?.passaporteOuNif },
]

function formatarData(v: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : v
}

function formatarDocumento(v: string): string {
  const d = v.replace(/\D/g, '')
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  return v
}

function apresentar(rotulo: string, valor: unknown): string | null {
  if (valor === null || valor === undefined || valor === '') return null
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não'
  const s = String(valor).trim()
  if (!s) return null
  if (rotulo === 'Data de nascimento') return formatarData(s)
  if (rotulo === 'CPF / CNPJ') return formatarDocumento(s)
  return s
}

interface Props {
  pessoa: Pessoa | null
  isOpen: boolean
  onClose: () => void
}

const DetalhesParticipante: React.FC<Props> = ({ pessoa, isOpen, onClose }) => {
  const rotuloCor = useColorModeValue('gray.500', 'gray.400')
  const vazioCor = useColorModeValue('gray.400', 'gray.600')
  const fundo = useColorModeValue('gray.50', 'gray.700')

  if (!pessoa) return null

  const preenchidos = CAMPOS.map(c => ({ ...c, valor: apresentar(c.rotulo, c.ler(pessoa)) }))
  const semDocumento = !apresentar('CPF / CNPJ', pessoa.customFields?.cnpj || pessoa.cpf)

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={2}>
          <Text fontSize="lg">{pessoa.nome || 'Participante'}</Text>
          {pessoa.credentialCode && (
            <Text fontSize="sm" fontFamily="mono" color={rotuloCor} fontWeight="normal">
              {pessoa.credentialCode}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {/*
            Sem documento é o caso do participante estrangeiro: o briefing manda conferir os
            dados pelo nome e pedir passaporte ou NIF no balcão. O aviso existe para o
            atendente não descobrir isso só depois de imprimir.
          */}
          {semDocumento && (
            <Box bg="orange.50" borderLeft="4px" borderColor="orange.400" px={4} py={3} mb={4} borderRadius="md">
              <Text fontSize="sm" color="orange.800" fontWeight="600">
                Sem CPF ou CNPJ no cadastro
              </Text>
              <Text fontSize="sm" color="orange.700">
                Confirme os dados pelo nome e solicite passaporte ou NIF.
              </Text>
            </Box>
          )}

          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={6} spacingY={4}>
            {preenchidos.map(({ rotulo, valor }) => (
              <Box key={rotulo}>
                <Text fontSize="xs" color={rotuloCor} textTransform="uppercase" fontWeight="600" mb={0.5}>
                  {rotulo}
                </Text>
                {valor ? (
                  <Text fontSize="md" wordBreak="break-word">
                    {valor}
                  </Text>
                ) : (
                  <Text fontSize="md" color={vazioCor} fontStyle="italic">
                    não informado
                  </Text>
                )}
              </Box>
            ))}
          </SimpleGrid>

          <Divider my={4} />

          <Box bg={fundo} borderRadius="md" px={4} py={3}>
            <Text fontSize="xs" color={rotuloCor} textTransform="uppercase" fontWeight="600" mb={1}>
              Credenciamento
            </Text>
            <Badge colorScheme={(pessoa.checkinCount || 0) > 0 ? 'green' : 'gray'}>
              {(pessoa.checkinCount || 0) > 0
                ? `Credenciado · ${pessoa.checkinCount} impressão(ões)`
                : 'Ainda não credenciado'}
            </Badge>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default DetalhesParticipante
